import { createElement } from "react";
import { createBrowserRouter, redirect } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";

import AppShell from "@/layout/app-shell";
import RootLayout, { AuthenticatedDashboardLayout } from "@/features/dashboard/layout";
import { LoaderElement } from "@/lib/react-router";
import { DashboardPage } from "@/features/dashboard/features/dashboard";
import {
  DashboardsOverviewPage,
  UnauthenticatedPage,
  dashboardsOverviewLoader,
} from "@/features/dashboard/features/general";
import { pageLoader, dashboardsLoader, rootLoader } from "@/features/dashboard/loaders";
import {
  dashboardLoader,
  dashboardLoaderId,
  dashboardsLoader as adminDashboardsLoader,
} from "@/features/admin/loader";
import {
  DashboardSettingsPage,
  DashboardsOverviewPage as DashboardsOverviewAdminPage,
} from "@/features/admin/features/dashboards";
import {
  EditPagePage,
  PagesOverviewPage,
  pageLoader as AdminPageLoader,
} from "@/features/admin/features/pages-management";
import {
  OrganisationDetailPage,
  OrganisationOverviewPage,
} from "@/features/admin/features/organisations";
import AdminDashboardLayout from "@/features/admin/layout/admin-dashboard-layout";
import AdminHeaderLayout from "@/features/admin/layout/admin-header-layout";
import { routes, ADMIN_APP_HANDLE } from "@/features/shared/navigation";
import { ErrorBoundary } from "@/features/shared/components";
import {
  loginLoader,
  CallbackPage,
  WithAuthentication,
  WithRole,
} from "@/features/shared/features/authentication";
import ExtranetsOverviewPage from "@/features/admin/features/extranets/pages/overview";
import ExtranetDetailPage from "@/features/admin/features/extranets/pages/details";
import { WarningsPageRoute } from "@/features/dashboard/features/dashboard/pages/dashboard-page";

import WarningPage from "./features/dashboard/features/general/pages/warning-page";

const initRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: routes.root,
      element: <AppShell />,
      ErrorBoundary,
      children: [
        // Unauthenticated Routes
        {
          element: <RootLayout />,
          children: [
            {
              path: routes.root,
              loader: rootLoader,
            },
            {
              path: routes.general.information,
              element: <UnauthenticatedPage />,
            },
          ],
        },
        // Auth Route
        {
          path: routes.auth.root,
          children: [
            {
              path: routes.auth.callback,
              element: <CallbackPage />,
            },
            {
              path: routes.auth.login,
              loader: loginLoader(queryClient),
              element: <LoaderElement />,
            },
          ],
        },
        // Dashboard Routes (Requires Authenticated User)
        {
          element: <AuthenticatedDashboardLayout />,
          children: [
            {
              path: routes.dashboards,
              element: <DashboardsOverviewPage />,
              loader: dashboardsOverviewLoader(queryClient),
            },
            {
              path: routes.dashboard,
              loader: dashboardsLoader(queryClient),
              element: null,
            },
            {
              path: routes.page,
              element: <DashboardPage />,
              loader: pageLoader(queryClient),
            },
            {
              path: routes.landWarnings,
              element: <WarningPage />,
            },
            {
              path: routes.maritimeWarnings,
              element: <WarningsPageRoute />,
            },
            {
              // Old maritime risk-picture URL. Kept so it does not fall through to /:page.
              path: "/:dashboard/maritime-risk-picture",
              loader: ({ params }) => redirect(`/${params.dashboard ?? ""}`),
            },
          ],
        },
        // Admin routes: shared Header + outer error boundary
        {
          element: createElement(
            WithAuthentication(WithRole(AdminHeaderLayout, ["admin"])),
          ),
          ErrorBoundary,
          children: [
            // Dashboard CMS (Menu + PageContainer + inner error boundary)
            {
              path: routes.admin.dashboard,
              element: <AdminDashboardLayout />,
              id: dashboardLoaderId,
              handle: ADMIN_APP_HANDLE,
              loader: dashboardLoader(queryClient),
              children: [
                {
                  path: routes.admin.dashboard_page,
                  element: <EditPagePage />,
                  loader: AdminPageLoader(queryClient),
                },
                {
                  path: routes.admin.dashboard_pages_overview,
                  element: <PagesOverviewPage />,
                },
                {
                  path: routes.admin.dashboard_settings,
                  element: <DashboardSettingsPage />,
                },
                {
                  path: "",
                  loader: () => redirect(routes.admin.dashboard_pages_overview),
                },
              ],
            },
            // Standalone admin pages (header shell only)
            {
              path: routes.admin.organisations,
              handle: ADMIN_APP_HANDLE,
              element: <OrganisationOverviewPage />,
            },
            {
              path: routes.admin.organistation_details,
              handle: ADMIN_APP_HANDLE,
              element: <OrganisationDetailPage />,
            },
            {
              path: routes.admin.extranets,
              handle: ADMIN_APP_HANDLE,
              element: <ExtranetsOverviewPage />,
            },
            {
              path: routes.admin.extranet_details,
              handle: ADMIN_APP_HANDLE,
              element: <ExtranetDetailPage />,
            },
            {
              path: routes.admin.home,
              handle: ADMIN_APP_HANDLE,
              loader: adminDashboardsLoader(queryClient),
              element: <DashboardsOverviewAdminPage />,
            },
          ],
        },
      ],
    },
  ]);

export default initRouter;
